import Papa from 'papaparse';
import { AmcLambdaFetch, awaitJSON } from '@src/utils/fetch-utils';
import {
  CategoryPathingData,
  AsinMap,
  AsinRow,
  CategoryRow,
  KpiSummary,
  ReportPeriod
} from '../category-pathing-types';
import {
  CATEGORY_PATHING_ENDPOINT,
  DEMO_CSV_PATHS,
  DEMO_REPORT_WINDOW_LABEL,
  mappingCsvUrl
} from '../category-pathing-constants';
import { formatPeriodLabel } from './format';

// --- Live pathing report (amc lambda) ---

export interface ApiAsinRow {
  newToBrandTrackedAsinPurchased: string;
  followupTrackedAsinPurchased: string;
  ntbUsersWithFollowup: number;
  avgDaysToFollowup: number | null;
}

interface CategoryPathingReportResponse {
  availablePeriods: ReportPeriod[];
  selectedPeriod: ReportPeriod | null;
  rows: ApiAsinRow[];
}

const fetchReport = async (
  amcInstanceId: string,
  reportEndDate?: string
): Promise<CategoryPathingReportResponse> => {
  const response = await AmcLambdaFetch(CATEGORY_PATHING_ENDPOINT, {
    params: { amcInstanceId, ...(reportEndDate ? { reportEndDate } : {}) }
  });
  return awaitJSON<CategoryPathingReportResponse>(response);
};

// --- ASIN → (product name, category) mapping (per-client CSV) ---

interface RawMappingRow {
  ASIN: string;
  Description: string;
  Segment: string;
}

const parseCsv = async <T>(url: string): Promise<T[]> => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch ${url}: ${response.status}`);
  const text = await response.text();
  return new Promise<T[]>((resolve, reject) => {
    Papa.parse<T>(text, {
      header: true,
      skipEmptyLines: true,
      complete: result => resolve(result.data),
      error: (error: Error) => reject(error)
    });
  });
};

const buildAsinMap = (rawRows: RawMappingRow[]): AsinMap => {
  const map: AsinMap = {};
  for (const row of rawRows) {
    const asin = row.ASIN?.trim();
    if (!asin) continue;
    map[asin] = {
      asin,
      productName: row.Description?.trim() ?? asin,
      category: row.Segment?.trim() ?? ''
    };
  }
  return map;
};

// --- Aggregation ---

interface PairAccumulator {
  ntb: string;
  fol: string;
  users: number;
  // avg_days is user-weighted, but only over rows that actually reported a value — a pair with
  // no timing data resolves to null so the UI shows "—" instead of "0 days".
  daysWeighted: number;
  daysUsers: number;
}

export const finalizeAvgDays = (a: PairAccumulator): number | null =>
  a.daysUsers > 0 ? a.daysWeighted / a.daysUsers : null;

// Each API row is a disjoint (entry, follow-up, first-switch) cohort, so aggregate to one row
// per (entry, follow-up) pair: sum users, user-weight avg_days_to_followup.
export const buildAsinRows = (apiRows: ApiAsinRow[]): AsinRow[] => {
  const agg = new Map<string, PairAccumulator>();
  for (const r of apiRows) {
    const ntb = r.newToBrandTrackedAsinPurchased?.trim() ?? '';
    const fol = r.followupTrackedAsinPurchased?.trim() ?? '';
    if (!ntb || !fol) continue;
    const users = Number(r.ntbUsersWithFollowup) || 0;
    const key = `${ntb}|||${fol}`;
    const prev = agg.get(key) ?? { ntb, fol, users: 0, daysWeighted: 0, daysUsers: 0 };
    prev.users += users;
    if (r.avgDaysToFollowup !== null) {
      prev.daysWeighted += users * r.avgDaysToFollowup;
      prev.daysUsers += users;
    }
    agg.set(key, prev);
  }
  return [...agg.values()].map(a => ({
    ntbAsin: a.ntb,
    followupAsin: a.fol,
    ntbUsersWithFollowup: a.users,
    avgDaysToFollowup: finalizeAvgDays(a),
    avgDaysUsers: a.daysUsers,
    isRepurchase: a.ntb === a.fol
  }));
};

// Category cross-sell rows are derived from the ASIN rows via the mapping: each ASIN maps to its
// category and the (entry-category, follow-up-category) pairs are aggregated the same way. Rows
// whose entry or follow-up ASIN isn't in the mapping can't be categorized and are dropped.
// The average is re-pooled weighting by each row's timed-user count (avgDaysUsers), not its total
// users, so category means stay correct as timing coverage varies across ASIN pairs.
export const deriveCategoryRows = (asinRows: AsinRow[], asinMap: AsinMap): CategoryRow[] => {
  const agg = new Map<string, PairAccumulator>();
  for (const r of asinRows) {
    const ntbCat = asinMap[r.ntbAsin]?.category?.trim();
    const folCat = asinMap[r.followupAsin]?.category?.trim();
    if (!ntbCat || !folCat) continue;
    const key = `${ntbCat}|||${folCat}`;
    const prev = agg.get(key) ?? {
      ntb: ntbCat,
      fol: folCat,
      users: 0,
      daysWeighted: 0,
      daysUsers: 0
    };
    prev.users += r.ntbUsersWithFollowup;
    prev.daysWeighted += r.avgDaysUsers * (r.avgDaysToFollowup ?? 0);
    prev.daysUsers += r.avgDaysUsers;
    agg.set(key, prev);
  }
  return [...agg.values()].map(a => ({
    ntbCategory: a.ntb,
    followupCategory: a.fol,
    ntbUsersWithFollowup: a.users,
    avgDaysToFollowup: finalizeAvgDays(a),
    avgDaysUsers: a.daysUsers,
    isRepurchase: a.ntb === a.fol
  }));
};

const computeCategories = (categoryRows: CategoryRow[]): string[] => {
  const totals = new Map<string, number>();
  for (const row of categoryRows) {
    totals.set(row.ntbCategory, (totals.get(row.ntbCategory) ?? 0) + row.ntbUsersWithFollowup);
    if (!totals.has(row.followupCategory)) {
      totals.set(row.followupCategory, 0);
    }
  }
  return [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([cat]) => cat);
};

const computeDeepDiveCategories = (asinRows: AsinRow[], asinMap: AsinMap): string[] => {
  const totals = new Map<string, number>();
  for (const row of asinRows) {
    const cat = asinMap[row.ntbAsin]?.category;
    if (!cat) continue;
    totals.set(cat, (totals.get(cat) ?? 0) + row.ntbUsersWithFollowup);
  }
  return [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([cat]) => cat);
};

export const computeKpis = (categoryRows: CategoryRow[]): KpiSummary => {
  const totalNtbUsers = categoryRows.reduce((s, r) => s + r.ntbUsersWithFollowup, 0);
  const totalRepurchaseUsers = categoryRows
    .filter(r => r.isRepurchase)
    .reduce((s, r) => s + r.ntbUsersWithFollowup, 0);
  const totalCrossSellUsers = totalNtbUsers - totalRepurchaseUsers;

  const entryTotals = new Map<string, number>();
  for (const r of categoryRows) {
    entryTotals.set(r.ntbCategory, (entryTotals.get(r.ntbCategory) ?? 0) + r.ntbUsersWithFollowup);
  }
  const topEntry = [...entryTotals.entries()].sort((a, b) => b[1] - a[1])[0];

  const topCrossSellRow = categoryRows
    .filter(r => !r.isRepurchase)
    .sort((a, b) => b.ntbUsersWithFollowup - a.ntbUsersWithFollowup)[0];

  let daysWeighted = 0;
  let daysUsers = 0;
  for (const r of categoryRows) {
    daysWeighted += r.avgDaysUsers * (r.avgDaysToFollowup ?? 0);
    daysUsers += r.avgDaysUsers;
  }
  const avgDaysToFollowup = daysUsers > 0 ? daysWeighted / daysUsers : null;

  return {
    totalNtbUsers,
    totalRepurchaseUsers,
    totalCrossSellUsers,
    topEntryCategory: { category: topEntry?.[0] ?? '', users: topEntry?.[1] ?? 0 },
    topCrossSellPair: {
      ntbCategory: topCrossSellRow?.ntbCategory ?? '',
      followupCategory: topCrossSellRow?.followupCategory ?? '',
      users: topCrossSellRow?.ntbUsersWithFollowup ?? 0,
      avgDays: topCrossSellRow?.avgDaysToFollowup ?? null
    },
    avgDaysToFollowup
  };
};

export const loadCategoryPathingData = async (
  amcInstanceId: string,
  cid: string,
  reportEndDate?: string
): Promise<CategoryPathingData> => {
  const [report, rawMapping] = await Promise.all([
    fetchReport(amcInstanceId, reportEndDate),
    parseCsv<RawMappingRow>(mappingCsvUrl(cid))
  ]);

  const asinMap = buildAsinMap(rawMapping);
  const asinRows = buildAsinRows(report.rows);
  const categoryRows = deriveCategoryRows(asinRows, asinMap);
  const categories = computeCategories(categoryRows);
  const deepDiveCategories = computeDeepDiveCategories(asinRows, asinMap);
  const kpis = computeKpis(categoryRows);

  return {
    asinRows,
    categoryRows,
    asinMap,
    categories,
    deepDiveCategories,
    kpis,
    reportWindowLabel: formatPeriodLabel(report.selectedPeriod),
    availablePeriods: report.availablePeriods,
    selectedPeriod: report.selectedPeriod
  };
};

// --- Demo (static CSV) variant ---
// Reproduces the pre-live-data behavior for the hidden comparison route: reads three CDN CSVs
// (ASIN pathing, category pathing, and the mapping) with no backend. Reuses the shared
// buildAsinMap / compute* helpers above; only the row parsing differs from the live path.

interface RawAsinRow {
  new_to_brand_tracked_asin_purchased: string;
  followup_tracked_asin_purchased: string;
  ntb_users_with_followup: string;
  avg_days_to_followup: string;
  filteredmetricsdiscriminatorcolumn: string;
}

interface RawCategoryRow {
  new_to_brand_asin_category_purchased: string;
  followup_asin_category_purchased: string;
  ntb_users_with_followup: string;
  avg_days_to_followup: string;
  filteredmetricsdiscriminatorcolumn: string;
}

// AMC's privacy discriminator: only rows where it's "false" are unaggregated/usable.
const isFilteredFalse = (value: unknown): boolean =>
  typeof value === 'string' && value.trim().toLowerCase() === 'false';

const buildAsinRowsFromCsv = (rawRows: RawAsinRow[]): AsinRow[] => {
  const agg = new Map<string, PairAccumulator>();
  for (const r of rawRows) {
    if (!isFilteredFalse(r.filteredmetricsdiscriminatorcolumn)) continue;
    const ntb = r.new_to_brand_tracked_asin_purchased?.trim() ?? '';
    const fol = r.followup_tracked_asin_purchased?.trim() ?? '';
    if (!ntb || !fol) continue;
    const users = Number(r.ntb_users_with_followup) || 0;
    const days = Number(r.avg_days_to_followup) || 0;
    const key = `${ntb}|||${fol}`;
    const prev = agg.get(key) ?? { ntb, fol, users: 0, daysWeighted: 0, daysUsers: 0 };
    prev.users += users;
    prev.daysWeighted += users * days;
    prev.daysUsers += users;
    agg.set(key, prev);
  }
  return [...agg.values()].map(a => ({
    ntbAsin: a.ntb,
    followupAsin: a.fol,
    ntbUsersWithFollowup: a.users,
    avgDaysToFollowup: finalizeAvgDays(a),
    avgDaysUsers: a.daysUsers,
    isRepurchase: a.ntb === a.fol
  }));
};

const buildCategoryRowsFromCsv = (rawRows: RawCategoryRow[]): CategoryRow[] => {
  const agg = new Map<string, PairAccumulator>();
  for (const r of rawRows) {
    if (!isFilteredFalse(r.filteredmetricsdiscriminatorcolumn)) continue;
    const ntb = r.new_to_brand_asin_category_purchased?.trim() ?? '';
    const fol = r.followup_asin_category_purchased?.trim() ?? '';
    if (!ntb || !fol) continue;
    const users = Number(r.ntb_users_with_followup) || 0;
    const days = Number(r.avg_days_to_followup) || 0;
    const key = `${ntb}|||${fol}`;
    const prev = agg.get(key) ?? { ntb, fol, users: 0, daysWeighted: 0, daysUsers: 0 };
    prev.users += users;
    prev.daysWeighted += users * days;
    prev.daysUsers += users;
    agg.set(key, prev);
  }
  return [...agg.values()].map(a => ({
    ntbCategory: a.ntb,
    followupCategory: a.fol,
    ntbUsersWithFollowup: a.users,
    avgDaysToFollowup: finalizeAvgDays(a),
    avgDaysUsers: a.daysUsers,
    isRepurchase: a.ntb === a.fol
  }));
};

export const loadCategoryPathingDemoData = async (): Promise<CategoryPathingData> => {
  const [rawAsin, rawCategory, rawMapping] = await Promise.all([
    parseCsv<RawAsinRow>(DEMO_CSV_PATHS.asin),
    parseCsv<RawCategoryRow>(DEMO_CSV_PATHS.categoryAll),
    parseCsv<RawMappingRow>(DEMO_CSV_PATHS.mapping)
  ]);

  const asinMap = buildAsinMap(rawMapping);
  const asinRows = buildAsinRowsFromCsv(rawAsin);
  const categoryRows = buildCategoryRowsFromCsv(rawCategory);
  const categories = computeCategories(categoryRows);
  const deepDiveCategories = computeDeepDiveCategories(asinRows, asinMap);
  const kpis = computeKpis(categoryRows);

  return {
    asinRows,
    categoryRows,
    asinMap,
    categories,
    deepDiveCategories,
    kpis,
    reportWindowLabel: DEMO_REPORT_WINDOW_LABEL,
    // The demo is a single static snapshot — no period picker.
    availablePeriods: [],
    selectedPeriod: null
  };
};
